import { PaletteMode, ThemeProvider, createTheme, darken, lighten } from '@mui/material'
import * as React from 'react'
import { FC, PropsWithChildren } from 'react'

import { colors } from '../constants/colors'
import { colorModeContext } from './color-context'

const placeholder = createTheme({})
const { breakpoints } = placeholder

const getDesignTokens = (mode: PaletteMode) => ({
  typography: {
    fontFamily: 'HCo Gotham',
    fontSize: 12,
    h1: {
      fontFamily: 'Fields',
      fontWeight: 700,
      fontSize: 58,
      lineHeight: 1.02,
      letterSpacing: '-0.02em',
      [breakpoints.down('md')]: {
        fontSize: 42
      }
    },
    h2: {
      fontWeight: 500,
      fontSize: 40,
      lineHeight: 1.05,
      letterSpacing: '-0.03em',
      [breakpoints.down('md')]: {
        fontSize: 20
      }
    },
    h3: {
      fontWeight: 500,
      fontSize: 34,
      lineHeight: 1.05,
      letterSpacing: '-0.03em',
      [breakpoints.down('md')]: {
        fontSize: 28
      }
    },
    h4: {
      fontWeight: 500,
      fontSize: 28,
      lineHeight: 1.1,
      letterSpacing: '-0.03em',
      [breakpoints.down('md')]: {
        fontSize: 24
      }
    },
    h5: {
      fontWeight: 500,
      fontSize: 24,
      lineHeight: 1.1,
      letterSpacing: '-0.03em',
      [breakpoints.down('md')]: {
        fontSize: 20
      }
    },
    h6: {
      fontWeight: 500,
      fontSize: 20,
      lineHeight: 1.1,
      letterSpacing: 0,
      [breakpoints.down('md')]: {
        fontSize: 15
      }
    },
    h7: {
      fontFamily: 'HCo Gotham',
      fontWeight: 500,
      fontSize: 18,
      lineHeight: 1.1,
      letterSpacing: 0,
      [breakpoints.down('md')]: {
        fontSize: 16
      }
    },
    body1: {
      fontWeight: 400,
      fontSize: 17,
      lineHeight: 1.35,
      letterSpacing: 0,
      [breakpoints.down('md')]: {
        fontSize: 15
      }
    },
    body2: {
      fontWeight: 400,
      fontSize: 15,
      lineHeight: 1.4,
      letterSpacing: 0,
      [breakpoints.down('md')]: {
        fontSize: 14
      }
    },
    subtitle1: {
      fontWeight: 400,
      fontSize: 16,
      [breakpoints.down('md')]: {
        fontSize: 14
      }
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: 14
    },
    caption: {
      fontWeight: 400,
      fontSize: 12
    },
    overline: {
      fontWeight: 500,
      fontSize: 12,
      textTransform: 'uppercase'
    }
  },
  shape: {
    borderRadius: 15
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          minWidth: 'auto',
          padding: `${placeholder.spacing(1.5)} ${placeholder.spacing(2.5)}`
        },
        sizeSmall: ({ theme }) => ({
          width: '240px',
          fontSize: 18,
          lineHeight: 2,
          [theme.breakpoints.down('sm')]: {
            width: '200px',
            fontSize: 16,
            lineHeight: 1.75
          }
        }),
        sizeMedium: {
          fontSize: 24
        },
        fullWidth: {
          padding: placeholder.spacing(2)
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: `${placeholder.spacing(1)} ${placeholder.spacing(2)}`,
          color: mode === 'light' ? colors.BLACK_100 : colors.DARK_MODE_100,
          background: mode === 'light' ? colors.WHITE : colors.DARK_MODE_70,
          boxShadow: 'rgba(0,0,0,1) 0 0 15px -10px',
          margin: '6px!important',
          whiteSpace: 'pre-wrap'
        },
        arrow: {
          color: mode === 'light' ? colors.BLACK_50 : colors.DARK_MODE_30
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: mode == 'dark' ? colors.DARK_MODE_70 : colors.WHITE
        }
      }
    }
  },
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: {
            main: colors.RED_100
          },
          secondary: {
            main: colors.GLACIER_BLUE_100,
            contrastText: colors.WHITE
          },
          tertiary: {
            main: colors.WHITE,
            dark: darken(colors.WHITE, 0.01)
          },
          info: {
            main: colors.BLACK_30
          },
          infoSecondary: {
            main: colors.BLACK_10,
            dark: colors.GEORGIAN_BAY_10,
            contrastText: colors.BLACK_100
          },
          text: {
            main: colors.BLACK_100,
            primary: colors.BLACK_100,
            secondary: colors.GEORGIAN_BAY_100
          },
          success: {
            main: colors.GLACIER_BLUE_100,
            contrastText: colors.WHITE
          },
          warning: {
            main: '#ffa22a'
          }
        }
      : {
          // palette values for dark mode
          primary: {
            main: colors.DARK_MODE_50
          },
          secondary: {
            main: colors.DARK_MODE_70
          },
          tertiary: {
            main: colors.DARK_MODE_70,
            dark: lighten(colors.DARK_MODE_70, 0.05),
            contrastText: colors.WHITE
          },
          info: {
            main: colors.DARK_MODE_10
          },
          infoSecondary: {
            main: colors.DARK_MODE_50,
            dark: darken(colors.DARK_MODE_50, 0.2)
          },
          text: {
            main: colors.WHITE,
            primary: colors.WHITE,
            secondary: colors.WHITE
          },
          success: {
            main: colors.DARK_MODE_30,
            contrastText: colors.DARK_MODE_80
          },
          warning: {
            main: '#c27e4d'
          }
        })
  }
})

const Theme: FC<PropsWithChildren> = ({ children }) => {
  const { mode } = colorModeContext()
  // Update the theme only if the mode changes
  // @ts-expect-error: existing type issue
  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode])

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary']
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary']
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true
    infoSecondary: true
    text: true
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h7: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    tertiary: true
  }
}

declare module '@mui/material/CircularProgress' {
  interface CircularProgressPropsColorOverrides {
    tertiary: true
  }
}

export default Theme
